import { NgClass } from "@angular/common";
import { Component, effect, inject, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, convertToParamMap, ParamMap, Params } from "@angular/router";
import { Observable, take } from "rxjs";
import { EvSalesmapLayoutComponent } from "src/app/shared-components/ev-salesmap-layout/ev-salesmap-layout.component";
import { MapRightMenuButtonsComponent } from "src/app/shared-components/map-right-menu-buttons/map-right-menu-buttons.component";
import { AreaStructure } from "src/models/area-builder/area-structure.model";
import { AreaType } from "src/models/area-builder/area-type.enum";
import { QueryArea } from "src/models/area-builder/query-area.model";
import { EstateAgentMarketShare, IResidenceSaleInArea, SaleInArea, SaleInAreaParams, StatisticsLine } from "src/models/estate/IResidenceSaleInArea";
import { FilterOverview } from "src/models/shared/filter.model";
import { EVEstateType } from "src/models/units/unit-attributes.model";
import { AreaBuilderDataService } from "src/services/area-builder-data.service";
import { EstateMapComponentsService } from "src/services/estate/estate-map-components.service";
import { FavoriteListDataService } from "src/services/estate/favorite-list-data.service";
import { MarketSalesDataService } from "src/services/estate/market-sales-data.service";
import { MarketSalesStateService } from "src/services/estate/market-sales-state.service";
import { RightSidebarOptions, RightSidebarService } from "src/services/right-sidebar.service";
import { UnitDataService } from "src/services/unit-data.service";
import { AreaBuilderSidebarComponentV2 } from "../../shared-components/area-builder-sidebar/area-builder-sidebar-v2.component";
import { EstateFavoriteListsComponent } from "../estate-favorite-lists/estate-favorite-lists.component";
import { EstateFilterComponent } from "../estate-filter/estate-filter.component";
import { EstateStatisticsComponent } from "../estate-statistics/estate-statistics.component";
import { EstateSaleInfocardComponent } from "./estate-sale-infocard/estate-sale-infocard.component";
import { EstateSalesListComponent } from "./estate-sales-list/estate-sales-list.component";
import { EvAreaType } from "src/app/infopage/models/area-type.enum";
import { MapLoadingOverlayComponent } from "../../shared-components/map-loading-overlay/map-loading-overlay.component";

const MAX_NR_OF_ITEMS_GREEN_BELT = 500;

@Component({
    selector: "app-estate-sale",
    imports: [
        EstateFilterComponent,
        EvSalesmapLayoutComponent,
        MapRightMenuButtonsComponent,
        EstateSalesListComponent,
        NgClass,
        AreaBuilderSidebarComponentV2,
        EstateStatisticsComponent,
        EstateSaleInfocardComponent,
        EstateFavoriteListsComponent,
        MapLoadingOverlayComponent,
    ],
    templateUrl: "./estate-sale.component.html",
    styleUrl: "./estate-sale.component.scss",
})
export class EstateSaleComponent implements OnDestroy, OnInit {
    private readonly marketSalesDataService = inject(MarketSalesDataService);
    private readonly marketSalesStateService = inject(MarketSalesStateService);
    private readonly estateMapComponentsService = inject(EstateMapComponentsService);
    private readonly rightSidebarService = inject(RightSidebarService);
    private readonly areaBuilderService = inject(AreaBuilderDataService);
    private readonly favoriteListDataService = inject(FavoriteListDataService);
    private readonly unitDataService = inject(UnitDataService);
    private readonly route = inject(ActivatedRoute);

    private rootQuery = { areaType: AreaType.Undefined, areaId: 0 };
    selectedSale = this.marketSalesDataService.selectedSale;
    miniInfocardEstate = this.marketSalesDataService.miniInfocardEstate;
    miniInfocardEstateImagePackages = this.marketSalesDataService.miniInfocardEstateImagePackages;
    protected readonly mapDataIsLoading = this.marketSalesDataService.mapDataIsLoading;

    selectedSidebar = this.rightSidebarService.selectedSidebar;
    rightSidebarOptions = RightSidebarOptions;
    areaStructure$: Observable<AreaStructure>;
    currentAreaStructure: AreaStructure;

    sales: SaleInArea[] = [];
    statistics: StatisticsLine[] = [];
    estateAgentMarketShares: EstateAgentMarketShare[] = [];
    favoriteLists: FilterOverview[] = [];

    constructor() {
        this.marketSalesStateService.loadUserFilter();

        effect(() => {
            if (this.marketSalesDataService.estateSaleIsLoading()) {
                const residenceSalesInArea: IResidenceSaleInArea = {
                    sales: [],
                    statistics: null,
                    estateAgentMarketShares: null,
                };
                this.estateMapComponentsService.updateLayers(residenceSalesInArea);
            }

            if (this.marketSalesDataService.saleResource.hasValue()) {
                this.statistics = this.marketSalesDataService.saleResource.value().statistics;
                this.sales = this.marketSalesDataService.saleResource.value().sales ?? [];
                this.estateAgentMarketShares = this.marketSalesDataService.saleResource.value().estateAgentMarketShares;
                this.sales = this.sales != null ? this.sales.sort(this.dynamicSort(this.marketSalesStateService.sortOrderSignal())) : [];
                this.sales = this.sales != null ? this.sales.slice(0, MAX_NR_OF_ITEMS_GREEN_BELT) : [];
                this.estateMapComponentsService.updateLayers(this.marketSalesDataService.saleResource.value());
            }
            if (this.marketSalesDataService.salePositionsResource.hasValue()) {
                this.sales = [];
                this.estateMapComponentsService.updateOverviewLayers(this.marketSalesDataService.salePositionsResource.value());
            }

            if (this.favoriteListDataService.favoriteListOverviewResource.hasValue()) {
                this.favoriteLists = this.favoriteListDataService.favoriteListOverviewResource.value();
            }
        });

        this.fetchAreaStructure(this.rootQuery);
    }

    ngOnInit(): void {
        this.route.queryParams.pipe(take(1)).subscribe((p: Params) => {
            const paramMap: ParamMap = convertToParamMap(p);

            const estate: SaleInAreaParams = {
                estateId: Number(paramMap.get("estateID") ?? paramMap.get("estateId")),
                unitId: Number(paramMap.get("unitID") ?? paramMap.get("unitId")),
                shareNumber: Number(paramMap.get("shareNumber")),
                estateShareId: Number(paramMap.get("estateShareId")),
                estateType: paramMap.has("estateTypeId") ? paramMap.get("estateTypeId") : null,
                flyTo: true,
            };
            if (estate.estateId != 0 || estate.estateShareId != 0 || estate.unitId != 0 || estate.shareNumber != 0 || estate.estateType != null) {
                this.marketSalesDataService.setSale(estate);
                this.selectEstateTypeInFilter(estate.unitId);
            } else if (paramMap.has("areaId") && paramMap.has("areaTypeId")) {
                const areaId = Number(paramMap.get("areaId"));
                const areaTypeId = Number(paramMap.get("areaTypeId"));
                this.areaBuilderService
                    .getAreaBoundary(areaId, areaTypeId)
                    .pipe(take(1))
                    .subscribe((area) => {
                        // Just use geoborder for housingcooperativ
                        if (areaTypeId === EvAreaType.HousingCooperative) {
                            this.areaBuilderService.positionMapToAreasAndPolygons([], area.geographicBorder);
                        } else {
                            this.areaBuilderService.positionMapToAreasAndPolygons([area], []);
                        }
                    });
            }
        });
    }

    // A user arriving from an external link expects to see the unit they clicked.
    //navigating around inside the map must never touch the filter.
    private selectEstateTypeInFilter(unitId: number): void {
        if (!unitId) return;

        this.unitDataService
            .getUnitNavigationInfoObservable(unitId)
            .pipe(take(1))
            .subscribe((unitNavigationInfo) => this.marketSalesStateService.ensureEstateTypeSelected(unitNavigationInfo?.estateType));
    }

    dynamicSort(property: string): (a: any, b: any) => number {
        let sortOrder = 1;
        if (property[0] === "-") {
            sortOrder = -1;
            property = property.substr(1);
        }
        return function (a, b) {
            const result = a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
            return result * sortOrder;
        };
    }

    fetchAreaStructure(query: QueryArea) {
        this.areaStructure$ = this.areaBuilderService.getAreaStructure(query);
        this.areaStructure$.subscribe((x) => (this.currentAreaStructure = x));
    }

    openFavoriteList(favoriteListId: number, hasNewAreas: boolean) {
        // return this.favoriteListV2DataService.setCurrentFavoriteList(favoriteListId, false, hasNewAreas);
    }

    ngOnDestroy(): void {
        this.estateMapComponentsService.cleanup();
    }
}
